import { AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePipelineRoundsQuery } from '../../hooks/usePipelineQueries';
import { useUpdateCVRoundMutation } from '../../hooks/useJobQueries';
import { TimelineStep } from './TimelineStep';

interface PipelineTimelineProps {
  pipelineId: string | null;
  pipelineName: string | null;
  currentRoundId: string | null;
  jobId: string;
  cvId: string;
  isRejected: boolean;
  workspaceId: string | undefined;
  idToken: string | undefined;
  variant?: 'card' | 'inline'; // 'card' = with card styling, 'inline' = no card styling
  disabled?: boolean; // prevent all round interactions
}

export function PipelineTimeline({
  pipelineId,
  pipelineName,
  currentRoundId,
  jobId,
  cvId,
  isRejected,
  workspaceId,
  idToken,
  variant = 'card',
  disabled = false,
}: PipelineTimelineProps) {
  const { t } = useTranslation('ats');
  const timelineTitle = pipelineName || t('pipelineTimeline.title');
  const {
    data: rounds,
    isLoading,
    error,
  } = usePipelineRoundsQuery({
    pipelineId: pipelineId || undefined,
    workspaceId,
    idToken,
    enabled: !!pipelineId,
  });

  const updateRoundMutation = useUpdateCVRoundMutation({
    jobId,
    cvId,
    workspaceId,
    idToken,
  });

  const handleRoundClick = (roundId: string) => {
    if (isRejected || updateRoundMutation.isPending || roundId === currentRoundId) {
      return;
    }
    updateRoundMutation.mutate(roundId);
  };

  // Conditional styling based on variant
  const containerClass =
    variant === 'card'
      ? 'bg-white rounded-lg border border-gray-200 p-5'
      : '';
  const titleClass =
    variant === 'card'
      ? 'text-lg font-semibold text-gray-900 mb-4'
      : 'text-sm font-semibold text-gray-700 mb-3';

  // No pipeline configured
  if (!pipelineId) {
    return (
      <div className={containerClass}>
        <h3 className={titleClass}>{timelineTitle}</h3>
        <p className="text-gray-500 text-sm">{t('pipelineTimeline.noPipeline')}</p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={containerClass}>
        <h3 className={titleClass}>{timelineTitle}</h3>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={containerClass}>
        <h3 className={titleClass}>{timelineTitle}</h3>
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="h-4 w-4" />
          <span>{error instanceof Error ? error.message : t('pipelineTimeline.loadFailed')}</span>
        </div>
      </div>
    );
  }

  // No rounds in pipeline
  if (!rounds || rounds.length === 0) {
    return (
      <div className={containerClass}>
        <h3 className={titleClass}>{timelineTitle}</h3>
        <p className="text-gray-500 text-sm">{t('pipelineTimeline.noRounds')}</p>
      </div>
    );
  }

  // Find current round index
  const currentRoundIndex = currentRoundId
    ? rounds.findIndex((r) => r.id === currentRoundId)
    : -1;

  const isClickable = !isRejected && !updateRoundMutation.isPending && !disabled;

  return (
    <div className={containerClass}>
      <h3 className={titleClass}>{timelineTitle}</h3>

      {/* Mutation error feedback */}
      {updateRoundMutation.isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {updateRoundMutation.error instanceof Error
            ? updateRoundMutation.error.message
            : t('pipelineTimeline.updateFailed')}
        </div>
      )}

      {/* Timeline - Responsive: vertical on mobile, horizontal on larger screens */}
      <div className="relative">
        {/* Mobile: Vertical layout */}
        <div className="flex flex-col gap-0 md:hidden">
          {rounds.map((round, index) => (
            <TimelineStep
              key={round.id}
              round={round}
              isCurrent={round.id === currentRoundId}
              isCompleted={currentRoundIndex >= 0 && index < currentRoundIndex}
              isLast={index === rounds.length - 1}
              isClickable={isClickable && round.id !== currentRoundId}
              onClick={() => handleRoundClick(round.id)}
              layout="vertical"
            />
          ))}
        </div>

        {/* Desktop: Horizontal scrollable layout */}
        <div
          className="hidden md:flex items-center overflow-x-auto py-4 px-2 scroll-smooth"
          style={{
            scrollbarWidth: 'thin',
            WebkitOverflowScrolling: 'touch',
            scrollbarColor: '#CBD5E1 transparent',
          }}
        >
          {rounds.map((round, index) => (
            <TimelineStep
              key={round.id}
              round={round}
              isCurrent={round.id === currentRoundId}
              isCompleted={currentRoundIndex >= 0 && index < currentRoundIndex}
              isLast={index === rounds.length - 1}
              isClickable={isClickable && round.id !== currentRoundId}
              onClick={() => handleRoundClick(round.id)}
              layout="horizontal"
            />
          ))}
        </div>
      </div>

      {/* Loading overlay during mutation */}
      {updateRoundMutation.isPending && (
        <div className="text-center text-sm text-gray-500 mt-2">
          <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
          {t('pipelineTimeline.updatingRound')}
        </div>
      )}
    </div>
  );
}
